import React, { useState } from 'react';
import { Modal, Form, Input, Button, Checkbox } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import BidhiMetadataTagsSelect from './bidhi/BidhiMetadataTagsSelect';
import { buildMetadataWithTags } from '../utils/bidhiEditByTag';

const { TextArea } = Input;

interface AddLineModalProps {
  visible: boolean;
  onCancel: () => void;
  tagOptions?: string[];
  onSubmit: (values: { lines: { content: string; isVisible?: boolean; metadata?: Record<string, unknown> }[] }) => Promise<void>;
}

const AddLineModal: React.FC<AddLineModalProps> = ({
  visible,
  onCancel,
  onSubmit,
  tagOptions = [],
}) => {
  const [form] = Form.useForm();
  const [lineBreakEnabled, setLineBreakEnabled] = useState(false);

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      // Process lines: split by newlines if lineBreakEnabled is true
      const processedValues = {
        ...values,
        lines: lineBreakEnabled
          ? values.lines?.flatMap((line: any) => {
              if (line.content && typeof line.content === 'string') {
                const contentLines = line.content.split('\n').filter((c: string) => c.trim());
                return contentLines.map((content: string) => ({
                  content: content.trim(),
                  isVisible: line.isVisible,
                  tags: line.tags,
                  metadata: line.metadata,
                }));
              }
              return line.content?.trim() ? [line] : [];
            }) || []
          : values.lines
              ?.filter((line: any) => line.content?.trim())
              .map((line: any) => ({
                ...line,
                metadata: buildMetadataWithTags(line.metadata || [], line.tags || []),
              })) || [],
      };

      if (lineBreakEnabled) {
        processedValues.lines = processedValues.lines.map((line: any) => ({
          content: line.content,
          isVisible: line.isVisible,
          metadata: buildMetadataWithTags(line.metadata || [], line.tags || []),
        }));
      }
      
      await onSubmit(processedValues);
      form.resetFields();
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  return (
    <Modal
      title="Add Lines"
      open={visible}
      onCancel={handleCancel}
      onOk={handleSubmit}
      width={900}
    >
      <Form form={form} layout="vertical">
        <div style={{ marginBottom: 16, padding: 8, background: '#f5f5f5', borderRadius: 4 }}>
          <Button
            size="small"
            type={lineBreakEnabled ? 'primary' : 'default'}
            onClick={() => setLineBreakEnabled(!lineBreakEnabled)}
          >
            Line Break: {lineBreakEnabled ? 'ON' : 'OFF'}
          </Button>
          <div style={{ fontSize: '12px', color: '#666', marginTop: 4 }}>
            When enabled, multi-line text will be split into separate lines
          </div>
        </div>
        <Form.List name="lines">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...restField }) => (
                <div key={key} style={{ border: '1px solid #f0f0f0', padding: 12, borderRadius: 4, marginBottom: 12 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px',
                      width: '100%',
                    }}
                  >
                    <Form.Item
                      {...restField}
                      name={[name, 'content']}
                      rules={[{ required: true, message: 'Missing content' }]}
                      style={{ flex: 1, marginBottom: 0 }}
                    >
                      <TextArea placeholder="Line content" rows={2} style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, 'isVisible']}
                      valuePropName="checked"
                      initialValue={true}
                      style={{ marginBottom: 0, flexShrink: 0 }}
                    >
                      <Checkbox>Visible</Checkbox>
                    </Form.Item>
                    <Button
                      onClick={() => remove(name)}
                      icon={<DeleteOutlined />}
                      style={{ flexShrink: 0 }}
                    />
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Tags</div>
                    <Form.Item
                      {...restField}
                      name={[name, 'tags']}
                      style={{ marginBottom: 0 }}
                    >
                      <BidhiMetadataTagsSelect tagOptions={tagOptions} />
                    </Form.Item>
                  </div>

                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8, fontWeight: 600 }}>Metadata</div>
                    <Form.List name={[name, 'metadata']}>
                    {(metaFields, { add: addMeta, remove: removeMeta }) => (
                      <>
                        {metaFields.map(({ key: metaKey, name: metaName, ...metaRestField }) => (
                          <div
                            key={metaKey}
                            style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'flex-start' }}
                          >
                            <Form.Item
                              {...metaRestField}
                              name={[metaName, 'key']}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Input placeholder="Metadata key" />
                            </Form.Item>
                            <Form.Item
                              {...metaRestField}
                              name={[metaName, 'value']}
                              style={{ flex: 1, marginBottom: 0 }}
                            >
                              <Input placeholder="Metadata value" />
                            </Form.Item>
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => removeMeta(metaName)}
                              style={{ marginTop: 4 }}
                            />
                          </div>
                        ))}
                        <div style={{ marginTop: 8 }}>
                          <Button
                            type="dashed"
                            size="small"
                            onClick={() => addMeta()}
                            icon={<PlusOutlined />}
                          >
                            Add Metadata
                          </Button>
                        </div>
                      </>
                    )}
                    </Form.List>
                  </div>
                </div>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add Line
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default AddLineModal;
